<template>
  <div class="row g-4">
    <div class="col-12">
      <router-link to="/work" class="btn btn-primary btn-no-r">
        <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
      </router-link>
      <router-link
        to="/work/tracking/history"
        class="btn btn-no-l btn-secondary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
    </div>
    <div class="col-lg-7">
      <div class="row">
        <div class="col-12">
          <div class="card p-4">
            <template v-if="data?.active?.length > 0">
              <div v-for="row in data.active" :key="row.id">
                <div class="d-flex justify-content-between">
                  <h6>Active Timer</h6>
                  <span class="badge badge-outline">{{ row.bucket }}</span>
                </div>
                <div class="timer-display mt-2" :key="timeKey">
                  {{ formatTime(elapsedTime(row.started_at), true) }}
                </div>
                <div class="small-muted">
                  <template v-if="row.is_automatic">
                    Automatic tracking from ActivityWatch
                  </template>
                  <template v-else>
                    {{ row.name ?? "Not categorized" }}
                  </template>
                </div>
                <div class="mt-3 d-flex gap-2">
                  <button
                    class="btn btn-primary btn-sm"
                    @click="stopActive(row.id)"
                  >
                    <i class="bi bi-pause"></i> Stop
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="d-flex justify-content-between">
                <h6>Active Timer</h6>
                <span class="badge badge-outline">No active timer</span>
              </div>
              <div class="px-4">
                <Vue3Lottie
                  :animationData="emptyJSON"
                  :height="128"
                  :width="300"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="g-4 mt-3">
        <div class="card p-4">
          <h5 class="mb-3">My Tasks</h5>
          <div
            v-if="!myTasksList || !myTasksList.length"
            class="text-center py-4 text-muted"
          >
            <i class="bi bi-check2-circle fs-1 d-block mb-2"></i>
            No tasks assigned to you.
          </div>
          <table v-else class="table align-middle">
            <thead>
              <tr>
                <th>Task</th>
                <th>Group</th>
                <th>Bucket</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in myTasksList" :key="task.id">
                <td>
                  <a
                    href="#"
                    class="fw-bold text-decoration-none"
                    @click.prevent="showTaskDetails(task)"
                    >{{ task.name }}</a
                  >
                </td>
                <td>
                  <span class="text-muted">{{
                    task.workTaskGroup?.name || "-"
                  }}</span>
                </td>
                <td>
                  <span class="text-muted">{{
                    task.workTaskGroup?.workBucket?.name || "-"
                  }}</span>
                </td>
                <td>
                  <span class="badge" :class="getStatusClass(task.status)">
                    {{ getStatusName(task.status) }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="getPriorityClass(task.priority)">
                    {{ getPriorityName(task.priority) }}
                  </span>
                </td>
                <td>
                  <div class="progress" style="height: 6px; width: 80px">
                    <div
                      class="progress-bar bg-success"
                      :style="{ width: (task.finished_percentage || 0) + '%' }"
                    ></div>
                  </div>
                  <small class="text-muted"
                    >{{ task.finished_percentage || 0 }}%</small
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="g-4 mt-3">
        <div class="card p-4">
          <h5 class="mb-3">My Tickets</h5>
          <div
            v-if="!myTickets || !myTickets.length"
            class="text-center py-4 text-muted"
          >
            <i class="bi bi-check2-circle fs-1 d-block mb-2"></i>
            No active tickets assigned to you.
          </div>
          <table v-else class="table align-middle">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in myTickets" :key="ticket.id">
                <td>
                  <span class="badge bg-secondary">#{{ ticket.id }}</span>
                  <div class="small fw-bold mt-1">{{ ticket.title }}</div>
                </td>
                <td>
                  <router-link
                    :to="'/messages?thread=' + ticket.message_thread_id"
                    class="text-decoration-none"
                  >
                    {{ ticket.thread?.subject || "View Conversation" }}
                  </router-link>
                  <div
                    class="small text-muted text-truncate"
                    style="max-width: 200px"
                  >
                    {{ ticket.description }}
                  </div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="getTicketStatusClass(ticket.status)"
                    >{{ getTicketStatusName(ticket.status) }}</span
                  >
                </td>
                <td>
                  <span
                    class="badge"
                    :class="getTicketPriorityClass(ticket.priority)"
                    >{{ ticket.priority }}</span
                  >
                </td>
                <td>
                  <router-link
                    :to="'/work/messages?thread=' + ticket.message_thread_id"
                    class="btn btn-sm btn-outline-primary"
                  >
                    <i class="bi bi-chat-dots"></i>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="col-lg-5">
      <div class="card p-4 h-100">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Work Analytics & Tracked Activity</h6>
          <span class="small-muted">This Week</span>
        </div>
        <div class="row mb-3">
          <div class="col-md-4">
            <div class="text-muted small">Total Worked</div>
            <div class="fs-4 fw-semibold text-success">
              {{ formatTime(totalAllTime) }}
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-muted small">Manual Tracking</div>
            <div class="fs-5 fw-semibold">
              {{ formatTime(manualTrackingTime) }}
            </div>
          </div>
          <div class="col-md-4">
            <div class="text-muted small">Warehouse Auto</div>
            <div class="fs-5 fw-semibold">
              {{ formatTime(autoTrackingTime) }}
            </div>
          </div>
        </div>
        <div class="progress mt-3" style="height: 8px">
          <div class="progress-bar bg-success" :style="{ width: weeklyProgressPercent + '%' }"></div>
        </div>
        <div class="small-muted mt-2" v-if="weeklyGoalHours > 0">{{ formatTime(Math.max(0, weeklyGoalHours * 3600 - totalAllTime)) }} remaining to weekly goal</div>
        <div class="small-muted mt-2" v-else>Set a weekly goal to track progress</div>
        <hr class="my-4" />
        <div class="row">
          <div class="col-md-6 mt-4">
            <div class="max-w-16rem mx-auto">
              <div class="day-labels mb-1">
                <span>M</span><span>T</span><span>W</span><span>T</span>
                <span>F</span><span>S</span><span>S</span>
              </div>
              <div class="activity-grid mb-3">
                <div v-for="week in last4Weeks" :key="week[0]" class="week-row">
                  <div
                    v-for="day in week"
                    :key="day"
                    :title="day"
                    class="heat-cell"
                    :class="'level-' + getHeatLevel(day)"
                  ></div>
                </div>
              </div>
            </div>
            <h6 class="mt-3 mb-3">Recent Activity</h6>
            <div class="small" v-if="recentActivity.length">
              <div
                class="d-flex mb-2"
                v-for="(activity, index) in recentActivity"
                :key="index"
              >
                <div class="me-2 text-muted">•</div>
                <div>
                  <div>{{ activity.description }}</div>
                  <div class="small-muted">{{ activity.timeAgo }}</div>
                </div>
              </div>
            </div>
            <div class="small text-muted" v-else>No recent activity</div>
          </div>
          <div class="col-md-6">
            <h6 class="mb-3">Warehouse Work</h6>

            <div
              class="mb-3"
              v-for="(time, category) in data?.tracked_times?.categories"
              :key="category"
            >
              <div class="d-flex justify-content-between small">
                <span>
                  <i
                    :class="{
                      'bi bi-box': category === 'picking',
                      'bi bi-box-seam': category === 'packing',
                      'bi bi-arrow-counterclockwise': category === 'returning',
                      'bi bi-box-arrow-in-down': category === 'sourcing',
                    }"
                  ></i>
                  {{ category.charAt(0).toUpperCase() + category.slice(1) }}
                </span>
                <span>{{ formatTime(time) }}</span>
              </div>
              <div class="progress" style="height: 6px">
                <div
                  class="progress-bar"
                  :class="{
                    'bg-success': category === 'packing',
                    'bg-warning': category === 'returning',
                    'bg-info': category === 'sourcing',
                  }"
                  :style="{ width: (time / totalTrackedTime) * 100 + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Details Modal -->
    <div
      v-if="showTaskModal"
      class="modal d-block"
      tabindex="-1"
      @click.self="showTaskModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedTask?.name }}</h5>
            <button class="btn-close" @click="showTaskModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Description</label>
              <p>{{ selectedTask?.description || "No description" }}</p>
            </div>
            <div class="row mb-3">
              <div class="col">
                <label class="form-label fw-bold">Group</label>
                <p>{{ selectedTask?.workTaskGroup?.name || "-" }}</p>
              </div>
              <div class="col">
                <label class="form-label fw-bold">Bucket</label>
                <p>
                  {{ selectedTask?.workTaskGroup?.workBucket?.name || "-" }}
                </p>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <label class="form-label fw-bold">Status</label>
                <div>
                  <select
                    class="form-select form-select-sm w-auto"
                    v-model.number="selectedTask.status"
                    @change="updateTaskStatus(selectedTask)"
                  >
                    <option :value="0">To Do</option>
                    <option :value="1">In Progress</option>
                    <option :value="2">On Hold</option>
                    <option :value="3">Completed</option>
                    <option :value="4">Cancelled</option>
                  </select>
                </div>
              </div>
              <div class="col">
                <label class="form-label fw-bold">Progress</label>
                <p>{{ selectedTask?.finished_percentage || 0 }}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import http from "@/modules/http";
import BaseIcon from "../../../components/icons/BaseIcon.vue";
import emptyJSON from "../../../assets/animations/emptyNotification.json";
import { Vue3Lottie } from "vue3-lottie";
export default {
  name: "MyWork",
  components: { BaseIcon, Vue3Lottie },
  created() {},
  data() {
    return {
      store: useStore(),
      data: undefined,
      timeKey: 0,
      emptyJSON: emptyJSON,
      myTickets: [],
      myTasksList: [],
      selectedTask: null,
      showTaskModal: false,
    };
  },
  computed: {
    totalTrackedTime() {
      if (!this.data?.tracked_times?.categories) return 1;
      return Object.values(this.data?.tracked_times?.categories || {}).reduce(
        (sum, t) => sum + t,
        0
      );
    },
    totalAllTime() {
      const categories = this.data?.tracked_times?.categories || {};
      const manual = this.data?.manual_times || {};
      const catSum = Object.values(categories).reduce((sum, t) => sum + t, 0);
      const manSum = Object.values(manual).reduce((sum, t) => sum + t, 0);
      const active = this.data?.active || [];
      const activeSum = active.reduce((sum, a) => {
        if (a.started_at) {
          return (
            sum +
            Math.floor((Date.now() - new Date(a.started_at).getTime()) / 1000)
          );
        }
        return sum;
      }, 0);
      return catSum + manSum + activeSum;
    },
    manualTrackingTime() {
      const manual = this.data?.manual_times || {};
      return Object.values(manual).reduce((sum, t) => sum + t, 0);
    },
    autoTrackingTime() {
      const categories = this.data?.tracked_times?.categories || {};
      return Object.values(categories).reduce((sum, t) => sum + t, 0);
    },
    last4Weeks() {
      const weeks = [];
      const today = new Date();

      // Find Monday of current week
      const dayOfWeek = today.getDay(); // Sunday = 0, Monday = 1
      const diffToMonday = (dayOfWeek + 6) % 7; // how many days back to Monday
      const currentMonday = new Date(today);
      currentMonday.setDate(today.getDate() - diffToMonday);

      for (let w = 0; w < 4; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
          const day = new Date(currentMonday);
          day.setDate(currentMonday.getDate() - w * 7 + d); // previous weeks
          const yyyy = day.getFullYear();
          const mm = String(day.getMonth() + 1).padStart(2, "0");
          const dd = String(day.getDate()).padStart(2, "0");
          week.push(`${yyyy}-${mm}-${dd}`);
        }
        weeks.push(week);
      }

      return weeks;
    },
    maxTrackedSeconds() {
      const days = this.data?.tracked_times?.days;
      if (!days) return 1;
      return Math.max(...Object.values(days).map((v) => parseFloat(v)));
    },
    weeklyGoalHours() {
      return this.store.state.preferences?.weekly_goal_hours || 40;
    },
    weeklyProgressPercent() {
      const goalSeconds = this.weeklyGoalHours * 3600;
      if (goalSeconds <= 0) return 0;
      return Math.min(100, Math.round((this.totalAllTime / goalSeconds) * 100));
    },
    warehouses() {
      return this.store.state.warehouses;
    },
    path() {
      return this.$route.path;
    },
    sessionId() {
      return this.store.state.sessionId;
    },
    filters() {
      return this.store.state.filters?.buckets;
    },
    recentActivity() {
      const activities = [];
      const active = this.data?.active || [];
      const tasks = this.myTasksList || [];

      for (const t of tasks.slice(0, 3)) {
        activities.push({
          description: `Working on "${t.name}"`,
          timeAgo: this.getTimeAgo(t.updated_at),
        });
      }

      for (const a of active.slice(0, 2)) {
        activities.push({
          description: `Active timer: ${a.name || a.bucket}`,
          timeAgo: this.getTimeAgo(a.started_at),
        });
      }

      return activities.slice(0, 4);
    },
  },
  mounted() {
    this.loadData();
    this.loadTickets();
    setInterval(() => this.timeKey++, 1010);
  },
  methods: {
    stopActive(id) {
      http.fetch("/work/times/stop", { id: id }).then(() => {
        this.loadData();
      });
    },
    loadTickets() {
      http.fetch("/tickets/my-tickets").then((data) => {
        this.myTickets = data;
      });
    },
    getTicketStatusClass(status) {
      const map = {
        open: "bg-info",
        pending: "bg-warning",
        solved: "bg-success",
        closed: "bg-secondary",
      };
      return map[status] || "bg-light";
    },
    getTicketStatusName(status) {
      const names = {
        open: "Open",
        pending: "Pending",
        solved: "Solved",
        closed: "Closed",
      };
      return names[status] || status;
    },
    getTicketPriorityClass(priority) {
      const map = {
        urgent: "bg-danger",
        high: "bg-warning text-dark",
        normal: "bg-info text-white",
        low: "bg-secondary text-white",
      };
      return map[priority] || "bg-light text-dark";
    },
    getPriorityClass(priority) {
      const map = {
        0: "bg-secondary",
        1: "bg-info",
        2: "bg-warning",
        3: "bg-danger",
      };
      return map[priority] || "bg-light";
    },
    getPriorityName(priority) {
      const names = {
        0: "Low",
        1: "Normal",
        2: "High",
        3: "Urgent",
      };
      return names[priority] || "Unknown";
    },
    getStatusClass(status) {
      const map = {
        0: "badge-outline",
        1: "bg-info",
        2: "bg-warning",
        3: "bg-success",
        4: "bg-danger",
      };
      return map[status] || "bg-light";
    },
    getStatusName(status) {
      const names = {
        0: "To Do",
        1: "In Progress",
        2: "On Hold",
        3: "Completed",
        4: "Cancelled",
      };
      return names[status] || "Unknown";
    },
    updateTicketStatus(ticket) {
      http.put(`/tickets/${ticket.id}`, { status: ticket.status });
    },
    updateTaskStatus(task) {
      http.fetch(
        `/work/tasks/${task.id}`,
        { status: task.status },
        true,
        "PUT"
      );
    },
    showTaskDetails(task) {
      this.selectedTask = task;
      this.showTaskModal = true;
    },
    getHeatLevel(date) {
      const seconds = parseFloat(this.data?.tracked_times?.days?.[date] || 0);
      const max = this.maxTrackedSeconds || 1;
      let level = Math.ceil((seconds / max) * 6);
      if (level > 6) level = 6;
      return level; // 0–6
    },
    loadData() {
      http.fetch("/work/tasks/my-tasks").then((data) => {
        this.data = data;
        this.myTasksList = data.tasks || [];
      });
    },
    getTimeAgo(dateString) {
      if (!dateString) return "Unknown";
      const seconds = Math.floor(
        (Date.now() - new Date(dateString).getTime()) / 1000
      );
      if (seconds < 60) return "Just now";
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    },
    elapsedTime(startDate) {
      const elapsedMilliseconds =
        new Date().getTime() - new Date(startDate).getTime();
      return Math.floor(elapsedMilliseconds / 1000);
    },
    formatTime(seconds, secunds = false) {
      if (seconds >= 60) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (secunds) {
          const remainingSeconds = seconds % 60;
          return `${hours}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
        }
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
      } else {
        return `${seconds} s`;
      }
    },
  },
};
</script>
<style scoped>
.timer-display {
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 1px;
}
.activity-grid {
  width: 100%;
}

.week-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 6px;
}

.heat-cell {
  padding-top: 100%;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  transition: 0.2s;
  background: #ebedf0;
}

.heat-cell:hover {
  transform: scale(1.1);
}

.level-0 {
  background: #e5e7eb;
}
.level-1 {
  background: #d8bae6;
}
.level-2 {
  background: #c89adf;
}
.level-3 {
  background: #b460dc;
}
.level-4 {
  background: #8548a3;
  color: #fff;
}
.level-5 {
  background: #632c7e;
  color: #fff;
}
.level-6 {
  background: #4f136d;
  color: #fff;
}
.level-7 {
  background: #351c41;
  color: #fff;
}
.level-8 {
  background: #1d0f23;
  color: #fff;
}

.warehouse-badge {
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 8px;
}
.small-muted {
  font-size: 13px;
  color: #6c757d;
}
.week-labels {
  display: grid;
  grid-template-rows: repeat(4, 1fr);
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}
.day-labels {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}
</style>
